import { SubmissionStatus } from '@bp/bp-graphql-types';
import { graphql } from 'client/gql';
import { Assignment } from 'components/Assignments/hooks/useAssignments';
import { BpFile } from 'components/Files/hooks/useFiles';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useConfirm } from 'hooks/useConfirm';
import { useDays } from 'hooks/useDays';
import { Room } from 'matrix-js-sdk';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { useRooms } from '../../../matrix/hooks/useRooms';

export const UpdateSubmissionsMutation = graphql(`
  mutation BpUpdateSubmissions($where: SubmissionWhere!, $update: SubmissionUpdateInput!) {
    updateSubmissions(where: $where, update: $update) {
      submissions {
        __typename
        uuid
        status
        public
      }
    }
  }
`);

const UseSubmissionsQuery = graphql(`
  query UseSubmissionsData($courseUuid: ID!, $assignmentUuid: ID!, $submissionUuid: ID) {
    submissions(
      where: { uuid: $submissionUuid, assignment: { uuid: $assignmentUuid, course: { uuid: $courseUuid } } }
      options: { sort: [{ updated: DESC }] }
    ) {
      __typename
      uuid
      status
      updated
      public
      owner {
        __typename
        uuid
        displayName
      }
      material {
        __typename
        uuid
        text
        fileEntries {
          __typename
          uuid
          filename
          mediaType
          size
          downloadToken
          publicDownloadToken
        }
      }
      feedbackMaterial {
        __typename
        uuid
        text
        fileEntries {
          __typename
          uuid
          filename
          mediaType
          size
          downloadToken
          publicDownloadToken
        }
      }
      assignment {
        __typename
        uuid
        title
        allowSubmissionAfterDueDate
        dueDate {
          __typename
          uuid
          date
        }
        course {
          __typename
          uuid
          name
          organization {
            __typename
            uuid
          }
          subject {
            __typename
            uuid
            name
            shortName
          }
        }
      }
    }
  }
`);

export type Submission = {
  uuid: string;
  status: SubmissionStatus;
  updated: Date;
  public: boolean;
  owner: {
    uuid: string;
    name: string;
  };
  material: {
    uuid: string;
    text: string | null;
    fileEntries: BpFile[];
  };
  feedbackMaterial: {
    uuid: string;
    text: string | null;
    fileEntries: BpFile[];
  } | null;
  assignment: Pick<Assignment, 'uuid' | 'title' | 'dueDate' | 'allowSubmissionAfterDueDate' | 'course'>;
  remainingDays: number;
  isEditable: boolean;
  room: Room | null;
  newMessage: boolean;
};

type useSubmissionsProps = {
  courseUuid: string;
  assignmentUuid: string;
  submissionUuid?: string;
};

export const useSubmissions = ({ courseUuid, assignmentUuid, submissionUuid }: useSubmissionsProps) => {
  const { t } = useTranslation();

  const { daysLeft } = useDays();

  const rooms = useRooms();

  const { ConfirmationDialog: ConfirmSubmitDialog, confirm: confirmSubmit } = useConfirm({
    defaultTitle: t('submissions.submit'),
    defaultMessage: t('submissions.confirmSubmit'),
  });

  const [{ data }, refetch] = useQuery({
    query: UseSubmissionsQuery,
    variables: { courseUuid, assignmentUuid, submissionUuid },
    pause: !assignmentUuid,
  });

  const submissions: Submission[] =
    data?.submissions.map((s) => {
      const remainingDays = daysLeft(dayjs(s.assignment.dueDate?.date).toISOString(), true);

      const isEditable =
        !(s.status === SubmissionStatus.New || s.status === SubmissionStatus.Done) &&
        (remainingDays > -1 || // Abgabe-Datum abgelaufen?
          (s.assignment.allowSubmissionAfterDueDate ?? false) || // oder Abgabe über das Datum hinaus erlauben
          s.status === SubmissionStatus.Feedback);

      const room = submissionUuid
        ? (rooms?.find((room) => {
            return room.name.includes(submissionUuid);
          }) ?? null)
        : null;

      const newMessage = room ? room.getRoomUnreadNotificationCount() > 0 : false;

      return {
        uuid: s.uuid,
        status: s.status,
        updated: dayjs(s.updated).toDate(),
        public: s.public ?? false,
        owner: {
          uuid: s.owner.uuid,
          name: s.owner.displayName ?? '',
        },
        material: {
          uuid: s.material.uuid,
          text: s.material.text ?? null,
          fileEntries: s.material.fileEntries.map((f) => ({
            uuid: f.uuid,
            filename: f.filename,
            mediaType: f.mediaType,
            size: f.size,
            downloadToken: f.downloadToken,
            publicDownloadToken: f.publicDownloadToken ?? null,
          })),
        },
        feedbackMaterial: s.feedbackMaterial
          ? {
              uuid: s.feedbackMaterial.uuid,
              text: s.feedbackMaterial.text ?? null,
              fileEntries: s.feedbackMaterial.fileEntries.map((f) => ({
                uuid: f.uuid,
                filename: f.filename,
                mediaType: f.mediaType,
                size: f.size,
                downloadToken: f.downloadToken,
                publicDownloadToken: f.publicDownloadToken ?? null,
              })),
            }
          : null,
        assignment: {
          uuid: s.assignment.uuid,
          title: s.assignment.title,
          allowSubmissionAfterDueDate: s.assignment.allowSubmissionAfterDueDate ?? false,
          dueDate: dayjs(s.assignment.dueDate?.date).toDate(),
          course: {
            uuid: s.assignment.course.uuid,
            title: s.assignment.course.name,
            organizationUuid: s.assignment.course.organization.uuid,
            subject: s.assignment.course.subject
              ? {
                  uuid: s.assignment.course.subject.uuid,
                  name: s.assignment.course.subject.name,
                  shortName: s.assignment.course.subject.shortName,
                }
              : null,
          },
        },
        remainingDays,
        isEditable,
        room,
        newMessage,
      };
    }) ?? [];

  function getSubmissionPath(submissionUuid: string): string {
    return `/courses/${courseUuid}/assignments/${assignmentUuid}/submissions/${submissionUuid}`;
  }

  function hasNewMessage(submissionUuid: string): boolean {
    const room = submissionUuid
      ? (rooms?.find((room) => {
          return room.name.includes(submissionUuid);
        }) ?? null)
      : null;

    return room ? room.getRoomUnreadNotificationCount() > 0 : false;
  }

  return {
    courseUuid,
    assignmentUuid,
    submissions,
    getSubmissionPath,
    hasNewMessage,
    refetch,
    ConfirmSubmitDialog,
    confirmSubmit,
  };
};
